import React, { useState } from 'react';
import { addSecureAccount, patchSecureAccount } from '../../Action/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Button
} from '@mui/material';

export default function SecureAccountAddDialog({ 
  open,
  onClose,
  onSuccess,
  form,
  onChange,
  showPassword,
  setShowPassword,
  formError,
  editIndex,
  isEditMode,
  statusOptions,
  mfaMethods,
  accessLevels
}) {
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Helper to encrypt password (stub, replace with real encryption if needed)
  const encryptPassword = (plain) => {
    // TODO: Replace with real encryption logic
    return plain ? btoa(plain) : '';
  };

  // Handle form submit for Add/Edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    setApiSuccess(null);
    setLoading(true);
    try {
      // Get user id from localStorage for createdBy
      const createdBy = localStorage.getItem('userId') || '';
      const now = new Date().toISOString();
      // Prepare API body
      const body = {
        appName: form.appName,
        accountName: form.accountName,
        status: form.status,
        username: form.username,
        registeredEmail: form.registeredEmail,
        registeredPhoneNumber: form.registeredPhone,
        accessLevel: form.accessLevel,
        assignedTo: form.assignedTo,
        mfaEnabled: !!form.mfaEnabled,
        mfaMethod: form.mfaMethod || '',
        credentialExpiryDate: form.credentialExpiry,
        passwordLastUpdated: form.passwordLastUpdated,
        rotationRequired: !!form.rotationRequired,
        createdBy,
        createdAt: now,
        updatedBy: createdBy,
        updatedAt: now,
        notes: form.notes || '',
      };

      if (form.password && form.password !== '********') {
        body.passwordEncrypted = encryptPassword(form.password);
      }

      if (isEditMode) {
        if (!form.secureAccountId) {
          setApiError('Secure account ID is missing. Please refresh and try again.');
          return;
        }
        // Edit mode: call patch API
        const res = await patchSecureAccount(form.secureAccountId, body);
        if (res.success) {
          setApiSuccess(res.message || 'Secure account updated successfully');
          setApiError(null);
          if (onSuccess) await onSuccess(); // Refresh table data
          if (onClose) onClose();
        } else {
          setApiError(res.message || 'Failed to update secure account');
        }
      } else {
        // Add mode: always call addSecureAccount
        const res = await addSecureAccount(body);
        if (res.success) {
          setApiSuccess(res.message || 'Secure account added successfully');
          setApiError(null);
          if (onSuccess) await onSuccess(); // Refresh table data
          if (onClose) onClose();
        } else {
          setApiError(res.message || 'Failed to add secure account');
        }
      }
    } catch (err) {
      setApiError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (event, reason) => {
    if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>{form.secureAccountId ? 'Edit Secure Account' : 'Add Secure Account'}</DialogTitle>
      <DialogContent dividers>
        {(formError || apiError) && <Alert severity="error" sx={{ mb: 2 }}>{formError || apiError}</Alert>}
        {apiSuccess && <Alert severity="success" sx={{ mb: 2 }}>{apiSuccess}</Alert>}
        <Grid container spacing={2}>
          {/* Username and Password at the top */}
          <Grid item xs={12} md={6}>
            <TextField label="Username *" name="username" value={form.username} onChange={onChange} fullWidth margin="dense" required placeholder="Enter account username" helperText="e.g. johndoe, alex123" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Password"
              name="password"
              value={form.password}
              onChange={onChange}
              fullWidth
              margin="dense"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <Button onClick={() => setShowPassword((v) => !v)} size="small">
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                )
              }}
              helperText="e.g. My$trongP@ssw0rd (Password is optional. Will be encrypted.)"
              placeholder="Enter account password (optional)"
            />
          </Grid>
          {/* Rest of the fields */}
          <Grid item xs={12} md={6}>
            <TextField label="App Name" name="appName" value={form.appName} onChange={onChange} fullWidth margin="dense" placeholder="Enter application name (e.g. Gmail, AWS)" helperText="e.g. Gmail, AWS, Salesforce" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Account Name" name="accountName" value={form.accountName} onChange={onChange} fullWidth margin="dense" placeholder="Enter account name or ID" helperText="e.g. john.smith, alex@company.com" />
          </Grid>
          {/* Removed Purpose and Description fields as per requirements */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Status</InputLabel>
              <Select name="status" value={form.status} onChange={onChange} label="Status" displayEmpty inputProps={{ 'aria-label': 'Status' }}>
                <MenuItem value="" disabled>Select status</MenuItem>
                {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Registered Email" name="registeredEmail" value={form.registeredEmail} onChange={onChange} fullWidth margin="dense" placeholder="Enter registered email address" helperText="e.g. john@gmail.com" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Registered Phone Number"
              name="registeredPhone"
              value={form.registeredPhone}
              onChange={onChange}
              fullWidth
              margin="dense"
              type="tel"
              inputProps={{ pattern: "[0-9]*", inputMode: "numeric" }}
              placeholder="Enter registered phone number"
              helperText="e.g. 9876543210"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={form.mfaEnabled} onChange={onChange} name="mfaEnabled" />}
              label="MFA Enabled"
            />
            {form.mfaEnabled && (
              <FormControl fullWidth margin="dense">
                <InputLabel>MFA Method</InputLabel>
                <Select name="mfaMethod" value={form.mfaMethod} onChange={onChange} label="MFA Method" displayEmpty inputProps={{ 'aria-label': 'MFA Method' }}>
                  <MenuItem value="" disabled>Select MFA method</MenuItem>
                  {mfaMethods.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
                <span style={{fontSize:'0.8em',color:'#888',marginLeft:'8px'}}>e.g. Authenticator App</span>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="access-level-label" shrink={true}>Access Level</InputLabel>
              <Select
                labelId="access-level-label"
                name="accessLevel"
                value={form.accessLevel || ''}
                onChange={onChange}
                label="Access Level"
                displayEmpty
                inputProps={{ 'aria-label': 'Access Level' }}
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
              >
                <MenuItem value="">
                  <span style={{ color: '#888' }}>Select access level</span>
                </MenuItem>
                {accessLevels.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
              <span style={{fontSize:'0.8em',color:'#888',marginLeft:'8px',marginTop:'4px',display:'block'}}>e.g. Admin</span>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Assigned To (User/Team)" name="assignedTo" value={form.assignedTo} onChange={onChange} fullWidth margin="dense" placeholder="Enter user or team name" helperText="e.g. Marketing Team, John Doe" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Credential Expiry Date" name="credentialExpiry" value={form.credentialExpiry} onChange={onChange} fullWidth margin="dense" type="date" InputLabelProps={{ shrink: true }} placeholder="Select credential expiry date" helperText="Auto-filled with 3 months from today" />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={form.rotationRequired} onChange={onChange} name="rotationRequired" />}
              label="Rotation Required"
            />
          </Grid>
          {/* Removed Created By, Created At, Updated By, Updated At, Last Accessed At fields from UI as per requirements */}
          <Grid item xs={12} md={12}>
            <TextField
              label="Notes"
              name="notes"
              value={form.notes || ''}
              onChange={onChange}
              fullWidth
              margin="dense"
              multiline
              minRows={6}
              maxRows={12}
              placeholder="Enter any additional details, context, or instructions for this account."
              helperText="e.g. This account is for quarterly reports."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {form.secureAccountId ? (loading ? 'Updating...' : 'Update') : (loading ? 'Adding...' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
